<template>
  <m-dialog
    ref="dialog"
    :value="value"
    hide-header
    hide-footer
    no-padding
    transparent-overlay
    zoom-in
    :max-width="$modalSizes.lg"
    max-height="100%"
    top="12vh"
    @input="input"
  >
    <div :class="['search-dialog', $store.state.breakpoint.smAndDown ? '-mobile' : '']">
      <div class="_search">
        <m-icon
          :type="isLoading ? 'loading' : 'search'"
          :spin="isLoading"
          :color="$colors.grey.base"
          size="20"
          class="_search-icon"
        />
        <m-text-field
          v-model:value="search"
          :placeholder="$t('searchDialog.searchAccount', { account: loggedInUserAccount.companyName })"
          hide-border
          hide-hover
          xl
          auto-focus
          @blur="$refs.dialog.focus()"
        />
        <m-btn
          v-if="$store.state.breakpoint.smAndDown"
          hide-border
          light
          @click="hide"
        >
          {{ $t('general.close') }}
        </m-btn>
      </div>
      <div
        v-if="showTabs"
        class="_tabs"
      >
        <m-tab
          v-for="tab in tabs"
          :key="tab.key"
          :title="tab.title"
          :badge="tab.badge"
          :active="kind === tab.kind"
          small
          @click="selectKind(tab.kind)"
        />
      </div>
      <div
        ref="resultsList"
        class="_results"
      >
        <m-endless-scroll-list
          :key="kind ?? 'all'"
          @visible="showMore"
        >
          <template
            v-for="section in sections"
            :key="section.key"
          >
            <div class="_heading">
              {{ section.title }}
              <span
                v-if="isSearching"
                class="_count"
              >{{ section.total }}</span>
            </div>
            <div
              v-for="row in section.rows"
              :key="row.key"
              :data-cy="`search-result-${row.kind}`"
              :class="['_item', isSearching ? '-detailed' : '-compact', row.index === focusedIndex ? '-focused' : '']"
              @click="goTo(row)"
              @mousemove="focusedKey = row.key"
            >
              <div class="_icon">
                <user-avatar
                  v-if="row.kind === userConfig.model"
                  :user="row.item"
                  :size="20"
                />
                <m-icon
                  v-else-if="row.kind === moreRowKind"
                  type="ellipsis"
                  :color="$colors.grey.base"
                />
                <m-icon-display
                  v-else-if="row.icon !== null"
                  :icon="row.icon.value"
                  :size="row.icon.size"
                  :show-background="row.icon.showBackground"
                />
              </div>
              <div class="_body">
                <div class="_title">
                  <span
                    v-for="(part, index) in row.titleParts"
                    :key="index"
                    :class="part.match ? '-match' : ''"
                  >{{ part.text }}</span>
                </div>
                <div
                  v-if="row.meta.length > 0"
                  class="_meta"
                >
                  <span
                    v-for="(text, index) in row.meta"
                    :key="index"
                    class="_meta-part"
                  >{{ text }}</span>
                </div>
              </div>
            </div>
          </template>
        </m-endless-scroll-list>
        <div
          v-if="isSearching && !isPending && !goalsFailed && rows.length === 0"
          class="_empty"
        >
          {{ $t('searchDialog.noResults', { search: query }) }}
        </div>
      </div>
    </div>
  </m-dialog>
</template>

<script>
import UserAvatar from 'shared/components/UserAvatar.vue';
import useDebounce from 'shared/composables/debounce';
import useGoalCycle from '@/composables/goal-cycle/goal-cycle';
import useGoalTypeProperty from '@/composables/customize-page/goal-type-property';
import useGoals from '@/composables/goal/goals';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePageVisits from '@/composables/page-visits';
import useProperties from '@/composables/property/property';
import useRepo from '@/nebula/repo';
import useSpaces from '@/composables/space/spaces';
import useUsers from '@/composables/user/users';
import { COUNT, RESULT } from 'shared/api/query/constants';
import { buildIcon, buildIconFromEntity, isEmptyIcon } from 'shared/lib/icon';
import { defaultExclusionCheck } from '@/composables/exclusion-checks';
import { displayName, email } from 'shared/composables/user';
import { findInArray } from 'shared/lib/array/array';
import {
  goal as goalConfig,
  gridPage as gridPageConfig,
  space as spaceConfig,
  user as userConfig,
} from 'shared/api/query/configs.json';
import { goalsById, goalsByTitle } from '@/api/query/nebula/goal';
import { groupVisitsByRecency, highlightMatches, matchesSearch, rankByMatch } from '@/lib/search-dialog';
import { isEmpty, isNullOrUndefined } from 'shared/lib/object/object';
import { ref } from 'vue';
import { routeName } from 'shared/constants.json';

const goalRowLimit = 10;
const rowLimit = 5;
const pageSize = 25;
const moreRowKind = 'more';
const kinds = ['goals', 'gridPages', 'spaces', 'users'];

const uniqueTitles = (entities) => [...new Set(entities.map(({ title }) => title))].join(', ');

const spacesIn = (propertyValues) => (propertyValues ?? []).flatMap((pv) => pv.spaces ?? []);

const usersIn = (propertyValues) => (propertyValues ?? []).flatMap((pv) => pv.users ?? []);

export default {
  name: 'SearchDialog',
  props: {
    value: {
      type: Boolean,
      required: false,
    },
  },
  emits: ['update:value'],
  setup() {
    const { debounce } = useDebounce();
    const { getEnrichedPageVisits } = usePageVisits();
    const search = ref('');
    const { spaces, selectSingle: selectSpace } = useSpaces();
    const { users, searchUsers, selectSingle: selectUser } = useUsers();
    const { goals, getGoals, selectSingle: selectGoal } = useGoals();
    const { entityList: gridPages } = useRepo(gridPageConfig.model);
    const { selectSingle: selectGoalCycle } = useGoalCycle();
    const { goalTypeProperty, goalTypeIcon } = useGoalTypeProperty();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { spaceProperty } = useProperties();

    return {
      debounce,
      getEnrichedPageVisits,
      search,
      spaces,
      selectSpace,
      users,
      searchUsers,
      selectUser,
      goals,
      getGoals,
      selectGoal,
      gridPages,
      selectGoalCycle,
      goalTypeProperty,
      goalTypeIcon,
      loggedInUserAccount,
      spaceProperty,
    };
  },
  components: { UserAvatar },
  data() {
    return {
      userConfig,
      moreRowKind,
      kind: null,
      visibleCount: pageSize,
      goalOrder: [],
      goalsSearch: '',
      goalsTotal: 0,
      goalsFailed: false,
      goalsLoading: false,
      focusedKey: null,
      recentlyVisitedItems: [],
      recentRequest: 0,
    };
  },
  computed: {
    query() {
      return this.search.trim();
    },
    isSearching() {
      return this.query !== '';
    },
    usesGoals() {
      return this.loggedInUserAccount.accountSettings.usesGoals;
    },
    isPending() {
      return this.isSearching && this.usesGoals && this.goalsSearch !== this.query;
    },
    isLoading() {
      return this.isPending || this.goalsLoading;
    },
    goalsComplete() {
      return this.goalsSearch === this.query && !this.goalsFailed;
    },
    goalMatches() {
      if (!this.isSearching || !this.usesGoals) {
        return [];
      }
      return this.goals.filter((goal) => isNullOrUndefined(goal.softDeletedAt) && matchesSearch(goal.title, this.query));
    },
    hasMoreGoals() {
      return this.goalsComplete && this.goalMatches.length < this.goalsTotal;
    },
    orderedGoalMatches() {
      const byUid = new Map(this.goalMatches.map((goal) => [goal.uid, goal]));
      const ordered = this.goalOrder.map((uid) => byUid.get(uid)).filter((goal) => goal !== undefined);
      const orderedUids = new Set(this.goalOrder);
      return [...ordered, ...rankByMatch(this.goalMatches.filter(({ uid }) => !orderedUids.has(uid)), this.query)];
    },
    matches() {
      const { query } = this;
      return {
        goals: this.orderedGoalMatches,
        users: rankByMatch(this.searchUsers(query), query, displayName),
        spaces: rankByMatch(this.spaces.filter((space) => matchesSearch(space.title, query)), query),
        gridPages: rankByMatch(this.gridPages.filter((gridPage) => matchesSearch(gridPage.title, query)), query),
      };
    },
    totals() {
      return {
        goals: this.goalsComplete ? this.goalsTotal : this.goalMatches.length,
        users: this.matches.users.length,
        spaces: this.matches.spaces.length,
        gridPages: this.matches.gridPages.length,
      };
    },
    kindsWithMatches() {
      return kinds.filter((kind) => this.matches[kind].length > 0);
    },
    showTabs() {
      return this.isSearching && (this.kind !== null || this.kindsWithMatches.length > 1);
    },
    tabs() {
      return [
        { key: 'all', kind: null, title: this.$t('general.all'), badge: '' },
        ...kinds
          .filter((kind) => this.kindsWithMatches.includes(kind) || this.kind === kind)
          .map((kind) => ({ key: kind, kind, title: this.kindTitle(kind), badge: `${this.totals[kind]}` })),
      ];
    },
    sections() {
      const sections = this.isSearching ? this.resultSections() : this.recentSections();
      let index = 0;
      return sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => {
          index += 1;
          return { ...row, index: index - 1 };
        }),
      }));
    },
    rows() {
      return this.sections.flatMap(({ rows }) => rows);
    },
    focusedIndex() {
      return Math.max(0, this.rows.findIndex(({ key }) => key === this.focusedKey));
    },
    memberCounts() {
      return this.users.reduce((counts, user) => {
        new Set(this.teamRefs(user).map(({ uid }) => uid)).forEach((uid) => counts.set(uid, (counts.get(uid) ?? 0) + 1));
        return counts;
      }, new Map());
    },
  },
  methods: {
    recentSections() {
      return groupVisitsByRecency(this.recentlyVisitedItems).map(({ key, items }) => ({
        key,
        title: this.$t(`searchDialog.groups.${key}`),
        rows: items.map((visit) => this.goalRow(this.selectGoal(visit.uid) ?? visit, { withPeople: false })),
      }));
    },
    resultSections() {
      if (this.kind !== null) {
        if (this.matches[this.kind].length === 0) {
          return [];
        }
        return [{
          key: this.kind,
          title: this.kindTitle(this.kind),
          total: this.totals[this.kind],
          rows: this.matches[this.kind].slice(0, this.visibleCount).map(this.toRow(this.kind)),
        }];
      }

      return this.kindsWithMatches.map((kind) => {
        const section = { key: kind, title: this.kindTitle(kind), total: this.totals[kind] };
        const rows = this.matches[kind].slice(0, kind === 'goals' ? goalRowLimit : rowLimit).map(this.toRow(kind));
        return { ...section, rows: section.total > rows.length ? [...rows, this.moreRow(section)] : rows };
      });
    },
    kindTitle(kind) {
      if (kind === 'goals') {
        return this.loggedInUserAccount.goalSettings.featureNamePlural;
      }
      return this.$t(`searchDialog.sections.${kind}`);
    },
    toRow(kind) {
      switch (kind) {
        case 'users':
          return this.userRow;
        case 'spaces':
          return this.spaceRow;
        case 'gridPages':
          return this.gridPageRow;
        default:
          return (goal) => this.goalRow(goal, { withPeople: true });
      }
    },
    moreRow(section) {
      return {
        key: `${moreRowKind}_${section.key}`,
        kind: moreRowKind,
        target: section.key,
        titleParts: [{ text: this.$t('searchDialog.showAll', { count: section.total }), match: false }],
        icon: null,
        meta: [],
      };
    },
    selectKind(kind) {
      this.kind = kind;
      this.visibleCount = pageSize;
      this.focusedKey = null;
      this.$refs.resultsList.scrollTop = 0;
    },
    showMore() {
      if (this.kind === null) {
        return;
      }
      if (this.matches[this.kind].length > this.visibleCount) {
        this.visibleCount += pageSize;
      }
      if (this.kind === 'goals' && this.hasMoreGoals && !this.goalsLoading && this.goalMatches.length < this.visibleCount + pageSize) {
        this.fetchGoals({ append: true });
      }
    },
    buildRow({ kind, item, title, icon = null, meta }) {
      return {
        key: `${kind}_${item.uid}`,
        kind,
        item,
        title,
        titleParts: highlightMatches(title, this.query),
        icon,
        meta: meta.filter((text) => text !== ''),
      };
    },
    goalRow(goal, { withPeople }) {
      const meta = [
        uniqueTitles(this.selectAll(spacesIn(goal.properties), this.selectSpace)),
        uniqueTitles(this.selectAll(goal.goalCycle ?? [], this.selectGoalCycle)),
      ];
      if (withPeople) {
        meta.push(this.selectAll(usersIn(goal.properties), this.selectUser).map((user) => displayName(user)).join(', '));
      }
      return this.buildRow({
        kind: goalConfig.model,
        item: goal,
        title: this.titleOf(goal),
        icon: this.goalIcon(goal),
        meta,
      });
    },
    userRow(user) {
      return this.buildRow({
        kind: userConfig.model,
        item: user,
        title: displayName(user),
        meta: [uniqueTitles(this.selectAll(this.teamRefs(user), this.selectSpace)), email(user)],
      });
    },
    spaceRow(space) {
      return this.buildRow({
        kind: spaceConfig.model,
        item: space,
        title: this.titleOf(space),
        icon: this.ownIcon(space),
        meta: [this.spacePath(space), this.$t('spaceTopBarMenu.members', { count: this.memberCounts.get(space.uid) ?? 0 })],
      });
    },
    gridPageRow(gridPage) {
      const creator = this.selectUser(gridPage.creator?.uid);
      return this.buildRow({
        kind: gridPageConfig.model,
        item: gridPage,
        title: this.titleOf(gridPage),
        icon: this.ownIcon(gridPage) ?? { value: buildIcon('AppstoreOutlined'), size: 18, showBackground: false },
        meta: [creator === undefined ? '' : displayName(creator)],
      });
    },
    titleOf(entity) {
      return isEmpty(entity.title) ? this.$t('list.noTitle') : entity.title;
    },
    ownIcon(entity) {
      const icon = buildIconFromEntity(entity);
      return icon === null || isEmptyIcon(icon) ? null : { value: icon, size: 18, showBackground: false };
    },
    selectAll(refs, select) {
      return refs.map(({ uid }) => select(uid)).filter((entity) => entity !== undefined);
    },
    teamRefs(user) {
      const teamPropertyUid = this.spaceProperty?.uid;
      if (teamPropertyUid === undefined) {
        return [];
      }
      return spacesIn((user.values ?? []).filter((pv) => pv.property?.uid === teamPropertyUid));
    },
    goalIcon(goal) {
      const ownIcon = this.ownIcon(goal);
      if (ownIcon !== null) {
        return ownIcon;
      }
      const typePropertyUid = this.goalTypeProperty?.uid;
      const properties = (goal.properties ?? []).filter((pv) => !isNullOrUndefined(pv.property));
      if (typePropertyUid === undefined || !properties.some((pv) => pv.property.uid === typePropertyUid)) {
        return null;
      }
      const typeIcon = this.goalTypeIcon({ ...goal, properties });
      return isEmptyIcon(typeIcon) ? null : { value: typeIcon, size: 16, showBackground: true };
    },
    spacePath(space) {
      const path = [];
      const visited = new Set([space.uid]);
      let parent = this.selectSpace(space.parents?.[0]?.uid);
      while (parent !== undefined && isNullOrUndefined(parent.archivedAt) && !visited.has(parent.uid)) {
        visited.add(parent.uid);
        path.unshift(parent.title);
        parent = this.selectSpace(parent.parents?.[0]?.uid);
      }
      return path.join(' / ');
    },
    isCompletelyLoaded(uid) {
      const goal = this.selectGoal(uid);
      return goal !== undefined && defaultExclusionCheck(goal);
    },
    buildRecentlyVisitedItems() {
      this.recentRequest += 1;
      const request = this.recentRequest;
      this.getEnrichedPageVisits().then((pageVisits) => {
        if (request !== this.recentRequest) {
          return;
        }
        this.recentlyVisitedItems = pageVisits.reduce((res, next) => {
          if (next.node?.type !== 'goal') {
            return res;
          }
          if (findInArray({ haystack: res, needle: next.node.uid }) !== null) {
            return res;
          }

          res.push({
            uid: next.node.uid,
            title: next.node.title,
            icon: next.node.icon,
            visitedAt: next.visitedAt,
          });
          return res;
        }, []);

        const missing = this.recentlyVisitedItems.map(({ uid }) => uid).filter((uid) => !this.isCompletelyLoaded(uid));
        if (missing.length === 0) {
          return;
        }
        // the stored visit already renders the row, loading only adds its cycles, teams and type icon
        this.getGoals({ queries: goalsById({ ids: missing, includeChildren: false, includeParents: false }) }).catch(() => {});
      });
    },
    input(value) {
      this.$emit('update:value', value);
    },
    retrieveResults() {
      if (this.query === '' || !this.usesGoals) {
        return;
      }
      this.fetchGoals({ append: false });
    },
    fetchGoals({ append }) {
      const { query } = this;
      const shownUids = this.orderedGoalMatches.map(({ uid }) => uid);
      const loadedMatches = this.goalMatches.filter((goal) => defaultExclusionCheck(goal)).length;
      this.goalsLoading = true;
      this.getGoals({
        queries: goalsByTitle(query, this.loggedInUserAccount.uid, RESULT, { page: 1, itemsPerPage: pageSize, countAlias: COUNT }),
        excludeLoaded: true,
      }).then((response) => {
        if (query !== this.query) {
          return;
        }
        // appended pages keep their place so the list does not reshuffle while scrolling
        const fetchedUids = response[RESULT].map(({ uid }) => uid).filter((uid) => !shownUids.includes(uid));
        this.goalOrder = append ? [...shownUids, ...fetchedUids] : [];
        this.goalsSearch = query;
        this.goalsTotal = loadedMatches + response[COUNT][0].count;
        this.goalsFailed = false;
      }).catch(() => {
        if (query !== this.query) {
          return;
        }
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        this.goalsSearch = query;
        this.goalsFailed = true;
      }).finally(() => {
        if (query === this.query) {
          this.goalsLoading = false;
        }
      });
    },
    focusRow(index) {
      this.focusedKey = this.rows[index].key;
      this.scrollFocusedItemIntoView();
    },
    moveFocusDown() {
      if (this.focusedIndex >= this.rows.length - 1) {
        return;
      }
      this.focusRow(this.focusedIndex + 1);
    },
    moveFocusUp() {
      if (this.focusedIndex === 0) {
        return;
      }
      this.focusRow(this.focusedIndex - 1);
    },
    scrollFocusedItemIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.resultsList;
        if (!list) {
          return;
        }
        const focusedElement = list.querySelectorAll('._item')[this.focusedIndex];
        if (focusedElement) {
          focusedElement.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    hide() {
      this.focusedKey = null;
      this.kind = null;
      this.$refs.dialog.focus();
      this.$refs.dialog.hide();
    },
    route({ kind, item }) {
      switch (kind) {
        case userConfig.model:
          return { name: routeName.profile, params: { userId: item.uid } };
        case spaceConfig.model:
          return { name: routeName.spaceDetails, params: { optionId: item.uid } };
        case gridPageConfig.model:
          return { name: routeName.dashboardDetails, params: { pageId: item.uid } };
        default:
          return { name: routeName.goalDetails, params: { goalId: item.uid } };
      }
    },
    goTo(row) {
      if (row.kind === moreRowKind) {
        this.selectKind(row.target);
        return;
      }
      this.$router.push(this.route(row));
      // hide() blurs the input, whose blur handler writes its DOM value back into search
      this.hide();
      this.search = '';
    },
    goToFocusedItem() {
      const row = this.rows[this.focusedIndex];
      if (row === undefined) {
        return;
      }

      this.goTo(row);
    },
    handleKeyDown(event) {
      if (!this.value) {
        return;
      }

      if (event.key === 'Enter') {
        this.goToFocusedItem();
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.code === 'ArrowDown' || event.which === 40) {
        event.preventDefault();
        event.stopPropagation();
        this.moveFocusDown();
        return;
      }

      if (event.code === 'ArrowUp' || event.which === 38) {
        event.preventDefault();
        event.stopPropagation();
        this.moveFocusUp();
      }
    },
  },
  watch: {
    query(query) {
      this.focusedKey = null;
      this.visibleCount = pageSize;
      this.goalOrder = [];
      this.goalsLoading = false;
      if (query === '') {
        this.kind = null;
      }
      this.debounce(this.retrieveResults, 700);
    },
    value(val) {
      if (val) {
        this.buildRecentlyVisitedItems();
      }
    },
  },
  created() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .search-dialog {
    display: flex;
    flex-direction: column;
    height: min(max(50vh, 57rem), calc(var(--viewport-height-100) - 24vh));

    ._search {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      padding: .6rem 1.4rem 0 2rem;

      ._search-icon {
        flex: 0 0 auto;
      }
    }

    ._tabs {
      display: flex;
      flex: 0 0 auto;
      gap: .4rem;
      padding: 0 1.4rem 0 1.6rem;
      border-bottom: 1px solid $border-color;
    }

    ._results {
      flex: 1 1 auto;
      padding: 0 1rem 1rem;
      overflow: auto;

      ._heading {
        display: flex;
        gap: .4rem;
        padding: 1.6rem 1rem .6rem;
        font-size: $font-size-2;
        font-weight: $font-weight-medium;
        color: $font-color-secondary;

        &:first-child {
          padding-top: .8rem;
        }

        ._count {
          font-weight: normal;
          color: $font-color-tertiary;
        }
      }

      ._item {
        display: flex;
        gap: 1rem;
        padding: 0 1rem;
        cursor: pointer;
        border-radius: .8rem;

        ._icon {
          display: flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
        }

        ._body {
          flex: 1 1 auto;
          min-width: 0;
        }

        ._title {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          .-match {
            font-weight: $font-weight-semibold;
          }
        }

        ._meta {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          ._meta-part + ._meta-part::before {
            margin: 0 .6rem;
            color: $font-color-tertiary;
            content: '•';
          }
        }

        &.-focused {
          background-color: $hover-color;
        }

        &.-compact {
          align-items: center;
          height: 3.6rem;

          ._body {
            display: flex;
            gap: .8rem;
            align-items: baseline;
          }

          ._title {
            flex: 0 1 auto;
            font-weight: $font-weight-medium;
          }

          ._meta {
            flex: 0 100 auto;
            min-width: 0;
            font-size: $font-size-3;
            color: $font-color-tertiary;
          }
        }

        &.-detailed {
          align-items: flex-start;
          padding-top: .8rem;
          padding-bottom: .8rem;

          ._title {
            line-height: 2rem;
          }

          ._meta {
            margin-top: .1rem;
            font-size: $font-size-2;
            line-height: 1.8rem;
            color: $font-color-secondary;
          }
        }
      }

      ._empty {
        padding: 4rem 1rem;
        color: $font-color-tertiary;
        text-align: center;
      }
    }

    &.-mobile {
      height: var(--viewport-height-100);
    }
  }
</style>
